import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { 
  MapPin, 
  Navigation, 
  Search, 
  CheckCircle, 
  AlertTriangle, 
  ChevronDown,
  Loader
} from 'lucide-react';
import { useLocation } from '../context/LocationContext';

const SimpleLocationPicker = () => {
  const { currentLocation, updateLocation, updateLocationByGPS, loading, error } = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [pincodeInput, setPincodeInput] = useState('');
  const [localError, setLocalError] = useState('');

  const popularCities = [
    { name: 'Hyderabad', pincode: '500034', state: 'Telangana' },
    { name: 'Visakhapatnam', pincode: '530041', state: 'Andhra Pradesh' },
    { name: 'Vijayawada', pincode: '520010', state: 'Andhra Pradesh' },
    { name: 'Bangalore', pincode: '560066', state: 'Karnataka' },
    { name: 'Chennai', pincode: '600096', state: 'Tamil Nadu' },
    { name: 'Mumbai', pincode: '400051', state: 'Maharashtra' }
  ];

  const handlePincodeSubmit = async (e) => {
    e.preventDefault();
    if (pincodeInput.length !== 6) {
      setLocalError('Please enter a valid 6-digit pincode');
      return;
    }
    
    try {
      setLocalError('');
      await updateLocation(pincodeInput);
      setPincodeInput('');
      setIsExpanded(false);
    } catch (err) {
      setLocalError(err.message);
    }
  };

  const handleGPS = async () => {
    try {
      setLocalError('');
      await updateLocationByGPS();
      setIsExpanded(false);
    } catch (err) {
      setLocalError(err.message);
    }
  };

  const handleCitySelect = async (city) => {
    try {
      setLocalError('');
      await updateLocation(city.pincode);
      setIsExpanded(false);
    } catch (err) {
      setLocalError(err.message);
    }
  };

  return (
    <div className="w-full max-w-sm">
      {/* Current Location Display - More Compact */}
      <Button
        variant="outline"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full justify-between border-orange-200 text-left h-10 px-3 hover:bg-orange-50 text-sm"
        disabled={loading}
      >
        <div className="flex items-center space-x-2">
          <MapPin className="w-4 h-4 text-orange-600 flex-shrink-0" />
          <div className="truncate">
            <span className="font-medium">
              {loading ? 'Getting location...' : (currentLocation?.city || 'Select City')}
            </span>
          </div>
        </div>
        {loading ? (
          <Loader className="w-3 h-3 animate-spin" />
        ) : (
          <ChevronDown className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
        )}
      </Button>

      {/* Expanded Location Picker - More Compact */}
      {isExpanded && (
        <Card className="absolute mt-1 shadow-lg border-orange-200 z-50 w-80 location-picker-dropdown">
          <CardContent className="p-3 space-y-3">
            {/* Quick Actions Row */}
            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={handleGPS}
                disabled={loading}
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 h-8 text-xs"
              >
                <Navigation className="w-3 h-3 mr-1" />
                Use GPS
              </Button>
              <Button
                variant="outline"
                onClick={() => setIsExpanded(false)}
                size="sm"
                className="h-8 text-xs"
              >
                Done
              </Button>
            </div>

            {/* Pincode Search - Compact */}
            <form onSubmit={handlePincodeSubmit} className="space-y-2">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3" />
                <Input
                  placeholder="Enter pincode"
                  value={pincodeInput}
                  onChange={(e) => {
                    setPincodeInput(e.target.value);
                    setLocalError('');
                  }}
                  className="pl-7 h-8 text-sm"
                  maxLength={6}
                  disabled={loading}
                />
              </div>
              {pincodeInput.length === 6 && (
                <Button 
                  type="submit" 
                  disabled={loading}
                  size="sm"
                  className="w-full bg-orange-600 hover:bg-orange-700 h-8 text-xs"
                >
                  Search
                </Button>
              )}
            </form>

            {/* Error Display - Compact */}
            {(localError || error) && (
              <div className="flex items-center space-x-1 p-2 bg-red-50 rounded border border-red-200">
                <AlertTriangle className="w-3 h-3 text-red-600 flex-shrink-0" />
                <div className="text-xs text-red-700">
                  {localError || error}
                </div>
              </div>
            )}

            {/* Popular Cities - Compact Grid */}
            <div>
              <h4 className="text-xs font-medium text-gray-700 mb-2">Popular Cities</h4>
              <div className="grid grid-cols-2 gap-1">
                {popularCities.slice(0, 6).map((city) => (
                  <Button
                    key={city.pincode}
                    variant="ghost"
                    onClick={() => handleCitySelect(city)}
                    disabled={loading}
                    className="justify-start h-8 p-2 text-left hover:bg-orange-50 text-xs"
                  >
                    <div className="truncate">
                      <div className="font-medium">{city.name}</div>
                    </div>
                  </Button>
                ))}
              </div>
            </div>

            {/* Selected Location Status - Compact */}
            {currentLocation && (
              <div className="border-t pt-2">
                <div className="flex items-center space-x-1 mb-1">
                  <CheckCircle className="w-3 h-3 text-green-600" />
                  <span className="text-xs font-medium text-green-900">Selected</span>
                </div>
                <div className="text-xs text-gray-600 mb-2 truncate">
                  {currentLocation.formattedAddress}
                </div>
                
                {/* Service Status - Compact Badges */}
                <div className="flex gap-1">
                  <Badge className="bg-green-100 text-green-700 text-[10px] px-1 py-0">
                    ✅ Doctors
                  </Badge>
                  <Badge className="bg-green-100 text-green-700 text-[10px] px-1 py-0">
                    ✅ Delivery
                  </Badge>
                  <Badge className="bg-green-100 text-green-700 text-[10px] px-1 py-0">
                    ✅ Lab Tests
                  </Badge>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SimpleLocationPicker;